import { useState } from "react";
import { Link, useLocation } from "wouter";
import { GHL_WEBHOOKS, submitOptIn } from "@/lib/ghl";
import GuideShell from "@/components/GuideShell";


/**
 * What is left of this page's own styling after the shell took over.
 *
 * `page`, `header` and `headerInner` used to live here: a 52px strip with a
 * context label and one "AI Tools" link, on a cream background, in a typeface
 * set on the wrapper. That strip carried no wordmark, no Resource Center link,
 * no account control and no way back — so somebody arriving from an email had
 * no signal they were on Your Smart Stack and nothing to click but the form.
 * GuideShell renders the real header and footer now, and the grid below is the
 * only layout this page still owns.
 */
const S = {
  grid: {
    maxWidth: 960,
    margin: "0 auto",
    padding: "4rem 1.5rem 5rem",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "4rem",
    alignItems: "start",
  } as React.CSSProperties,
};

export default function LittlebirdOptin() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [, navigate] = useLocation();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      await submitOptIn(GHL_WEBHOOKS.littlebirdQuickWins, {
        firstName: firstName.trim(),
        email: email.trim(),
        source: "littlebird-quickwins",
      });
      setStatus("success");
      // Redirect to Littlebird resources after a brief confirmation moment
      setTimeout(() => navigate("/resources/littlebird/quick-wins"), 2500);
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again.");
    }
  }

  return (
    <GuideShell context="Quick Wins · Littlebird" backHref="/resources/littlebird" backLabel="Back to Littlebird">

      <div className="optin-grid" style={S.grid}>

        {/* ── LEFT: Copy ── */}
        <div>
          <p style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600, color: "#9C9690", marginBottom: "1rem" }}>
            A free guide for people who want to work smarter, not harder
          </p>
          <h1 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 400, color: "#2C2A27", lineHeight: 1.2, marginBottom: "1rem" }}>
            Finally.{" "}
            <em style={{ fontStyle: "italic", color: "#5a8a6a" }}>A memory layer that asks almost nothing of you.</em>
          </h1>
          <p style={{ fontSize: 15, color: "#4a3a2a", lineHeight: 1.8, marginBottom: "2rem" }}>
            Littlebird runs quietly in the background, capturing your meetings, your screen, your work, so when you need something, it's already there. This guide walks you through three Quick Wins that show you exactly how to put it to work.
          </p>

          {/* What's inside */}
          <div style={{ borderLeft: "2px solid #5a8a6a", paddingLeft: "1.25rem", marginBottom: "2rem" }}>
            <p style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, color: "#2C2A27", marginBottom: "0.75rem" }}>
              What's inside
            </p>
            {[
              "How to prepare for any meeting in seconds, using your own history",
              "How to get a weekly productivity mirror without tracking a single thing",
              "How to set Routines that run quietly every day without your involvement",
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: "0.75rem" }}>
                <span style={{ color: "#5a8a6a", fontWeight: 700, fontSize: 13, marginTop: 1, flexShrink: 0 }}>✓</span>
                <p style={{ fontSize: 14, color: "#4a3a2a", lineHeight: 1.65, margin: 0 }}>{item}</p>
              </div>
            ))}
          </div>

          {/* About: image + text */}
          <div style={{ borderTop: "1px solid #E8E2D9", paddingTop: "1.5rem" }}>
            <p style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, color: "#9C9690", marginBottom: "1rem" }}>
              About Your Smart Stack
            </p>
            <div style={{ display: "flex", gap: "1.25rem", alignItems: "flex-start" }}>
              <div>
                <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 18, fontWeight: 400, color: "#2C2A27", marginBottom: "0.2rem" }}>
                  Your Smart Stack
                </p>
                <p style={{ fontSize: 12, color: "#6B6560", fontStyle: "italic", marginBottom: "0.65rem" }}>
                  Plain-English AI guides for business owners.
                </p>
                <p style={{ fontSize: 13, color: "#4a3a2a", lineHeight: 1.7, marginBottom: "0.65rem" }}>
                  Your Smart Stack exists because too much AI content talks at people instead of with them. This guide is the starting point: a calm, honest introduction to Littlebird that respects your intelligence and your pace.
                </p>
                <p style={{ fontSize: 11, color: "#9C9690" }}>
                  Smart Stack ·{" "}
                  <a href="https://carenglasser.com" target="_blank" rel="noopener noreferrer" style={{ color: "#9C9690", textDecoration: "underline" }}>
                    carenglasser.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Form card (sticky) ── */}
        {/* 124px clears the sticky header: 64px for the wordmark row plus 44px
            for the back row, and a gap. It was 80, which was right when the
            header was one row and the back link scrolled away with the page. */}
        <div style={{ position: "sticky", top: 124 }}>
          {status === "success" ? (
            <div style={{ background: "#FFFFFF", border: "1px solid #E8E2D9", borderRadius: 18, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", padding: "2.25rem 2.5rem", textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#E8F5F3", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.25rem", fontSize: 24 }}>
                🐦
              </div>
              <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 24, fontWeight: 400, color: "#2C2A27", marginBottom: "0.75rem" }}>
                You're in.
              </h2>
              <p style={{ fontSize: 14, color: "#6B6560", lineHeight: 1.75, marginBottom: "0.5rem" }}>
                Your Littlebird Quick Wins guide is on its way to your inbox. Check your email. It arrives immediately.
              </p>
              <p style={{ fontSize: 12, color: "#9C9690", marginBottom: "1.5rem" }}>
                No spam. No sequences you didn't ask for. Just the guide.
              </p>
              <div style={{ borderTop: "1px solid #E8E2D9", paddingTop: "1.25rem" }}>
                <Link href="/resources/littlebird" style={{ fontSize: 13, color: "#13335A", fontWeight: 600, textDecoration: "none" }}>
                  Explore all Littlebird resources →
                </Link>
              </div>
            </div>
          ) : (
            <div style={{ background: "#FFFFFF", border: "1px solid #E8E2D9", borderRadius: 18, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", padding: "2.25rem 2.5rem" }}>
              {/* Card header */}
              <div style={{ marginBottom: "1.75rem" }}>
                <p style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, color: "#9C9690", marginBottom: "0.4rem" }}>
                  Free Guide
                </p>
                <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 24, fontWeight: 400, color: "#2C2A27", lineHeight: 1.25, marginBottom: "0.4rem" }}>
                  Littlebird Quick Wins
                </h2>
                <p style={{ fontSize: 13, color: "#6B6560" }}>Three wins. One shift in how you work.</p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={{ display: "block", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, color: "#5a4a3a", marginBottom: 6 }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Your first name"
                    required
                    style={{ width: "100%", border: "1px solid #D8D0C4", borderRadius: 8, padding: "10px 14px", fontSize: 14, color: "#2C2A27", background: "#FDFBF8", outline: "none", boxSizing: "border-box", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "#13335A"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(42,157,143,0.12)"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "#D8D0C4"; e.currentTarget.style.boxShadow = "none"; }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, color: "#5a4a3a", marginBottom: 6 }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    style={{ width: "100%", border: "1px solid #D8D0C4", borderRadius: 8, padding: "10px 14px", fontSize: 14, color: "#2C2A27", background: "#FDFBF8", outline: "none", boxSizing: "border-box", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "#13335A"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(42,157,143,0.12)"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "#D8D0C4"; e.currentTarget.style.boxShadow = "none"; }}
                  />
                </div>

                {errorMsg && <p style={{ fontSize: 13, color: "#c0392b" }}>{errorMsg}</p>}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  style={{ width: "100%", background: "#1a3a2a", color: "#FFFFFF", border: "none", borderRadius: 8, padding: "13px 20px", fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", cursor: status === "loading" ? "not-allowed" : "pointer", opacity: status === "loading" ? 0.7 : 1, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", marginTop: 4, transition: "background 0.2s ease" }}
                  onMouseEnter={(e) => { if (status !== "loading") e.currentTarget.style.background = "#2a5a3a"; }}
                  onMouseLeave={(e) => { if (status !== "loading") e.currentTarget.style.background = "#1a3a2a"; }}
                >
                  {status === "loading" ? "Sending…" : "Send me the free guide →"}
                </button>
              </form>

              <p style={{ fontSize: 11, color: "#9C9690", textAlign: "center", marginTop: 14 }}>
                No spam. No sequences you didn't ask for. Just the guide.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Responsive: collapse to single column on mobile */}
      <style>{`
        @media (max-width: 680px) {
          .optin-grid { grid-template-columns: 1fr !important; gap: 2.5rem !important; }
        }
      `}</style>

    </GuideShell>
  );
}
